import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, TextField, InputAdornment, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRecoilCallback, useSetRecoilState } from 'recoil';
import { Stop } from '@app/shared/models/tour.type';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_mapRef } from '@app/shared/state/map-ref.atom';
import { passiveCollector } from '@app/shared/services/passive-data/passive-data-collector';

interface MapStopSearchProps {
  stops: Stop[];
  mainColor: string;
}

export const MapStopSearch = React.memo(function MapStopSearch({ stops, mainColor }: MapStopSearchProps) {
  const { t } = useTranslation();
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const setActiveStopState = useSetRecoilState(atom_activeStopState);

  const getMapRef = useRecoilCallback(({ snapshot }) => () =>
    snapshot.getLoadable(atom_mapRef).getValue()
  , []);

  const filteredStops = useMemo(() => {
    if (!searchQuery.trim() || !stops) return [];
    const query = searchQuery.trim().toLowerCase();
    return stops.filter((stop) =>
      stop.s_title?.toLowerCase().includes(query)
    ).slice(0, 8);
  }, [searchQuery, stops]);

  useEffect(() => {
    if (isSearchActive) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isSearchActive]);

  useEffect(() => {
    if (!isSearchActive) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setIsSearchActive(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchActive]);

  const handleSearchStopSelect = (stop: Stop) => {
    passiveCollector.trackSearch(stop.s_title || stop.id);
    setIsSearchActive(false);
    setSearchQuery('');
    setActiveStopState(stop);

    const mapRef = getMapRef();
    if (mapRef && stop.stop_location) {
      mapRef.flyTo([stop.stop_location.latitude, stop.stop_location.longitude], 17, {
        animate: true,
        duration: 1.0,
      });
    }
  };

  return (
    <Box ref={searchContainerRef} sx={{ position: 'relative' }}>
      {!isSearchActive ? (
        <Box
          onClick={() => setIsSearchActive(true)}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            height: 36,
            borderRadius: '20px',
            bgcolor: '#fff',
            border: '1px solid',
            borderColor: '#E0E0E0',
            px: 1.5,
          }}
          aria-label={t('search_stops') || 'Search'}
        >
          <i className="fa-regular fa-magnifying-glass" style={{ fontSize: 13, color: '#333' }} />
          <Typography sx={{ fontSize: 13, color: '#333', userSelect: 'none' }}>
            {t('search_stops')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            inputRef={searchInputRef}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('search_stops_placeholder') || 'Search stops...'}
            autoComplete="off"
            size="small"
            sx={{
              width: 250,
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                bgcolor: '#fff',
                border: '1px solid',
                borderColor: mainColor,
                '& fieldset': { border: 'none' },
                fontSize: 14,
                height: 36,
                px: '4px',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ ml: 0.5 }}>
                  <i className="fa-regular fa-magnifying-glass" style={{ fontSize: 13, color: '#999' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <Box
                    onClick={() => setSearchQuery('')}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      bgcolor: '#e0e0e0',
                      '&:hover': { bgcolor: '#bdbdbd' },
                    }}
                  >
                    <i className="fa-solid fa-xmark" style={{ fontSize: 9, color: '#666' }} />
                  </Box>
                </InputAdornment>
              ) : null,
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsSearchActive(false);
                setSearchQuery('');
              }
            }}
          />
        </Box>
      )}

      {isSearchActive && searchQuery.trim().length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            mt: 0.5,
            width: 250,
            maxHeight: 240,
            overflowY: 'auto',
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            zIndex: 1000,
            py: 0.5,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { borderRadius: 3, bgcolor: '#f0f0f0' },
            '&::-webkit-scrollbar-thumb': { borderRadius: 3, bgcolor: '#ccc', '&:hover': { bgcolor: '#aaa' } },
          }}
        >
          {filteredStops.length > 0 ? (
            filteredStops.map((stop, idx) => (
              <Box
                key={stop.id || idx}
                onClick={() => handleSearchStopSelect(stop)}
                sx={{
                  px: 1.5,
                  py: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                {stop.config?.marker ? (
                  <img src={stop.config.marker} alt="" style={{ width: 20, height: 20, objectFit: 'contain', flexShrink: 0 }} />
                ) : stop.config?.pin ? (
                  <img src={stop.config.pin} alt="" style={{ width: 20, height: 20, objectFit: 'contain', flexShrink: 0 }} />
                ) : stop.icon ? (
                  <i className={`fa-solid fa-${stop.icon}`} style={{ color: stop.config?.color || mainColor, fontSize: 14, flexShrink: 0 }} />
                ) : (
                  <i className="fa-solid fa-location-dot" style={{ color: mainColor, fontSize: 14, flexShrink: 0 }} />
                )}
                <Typography
                  sx={{
                    fontSize: 13,
                    color: '#333',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: stop.s_title.replace(
                      new RegExp(`(${searchQuery.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
                      `<span style="color:${mainColor};font-weight:600">$1</span>`
                    )
                  }}
                />
              </Box>
            ))
          ) : (
            <Typography sx={{ textAlign: 'center', color: '#999', py: 1.5, fontSize: 13 }}>
              {t('no_results_found')}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
});
